import React, { useState, useCallback } from 'react';
import Select, { OptionProps, components, Props as SelectProps } from 'react-select';
import AsyncSelect from 'react-select/async';
import useViewport from '@client/desktop/customHooks/useViewport';
import { noop } from '@client/desktop/utils/functions.utils';
import { debounce } from '@client/desktop/utils/debounce';
import { ToastMsg } from '../../Toast/ToastTitle';
import { IMultiSelect, Toptions } from './type';
import './Select.css';

const SearchableSelect: React.FC<IMultiSelect> = (props) => {
	const [showError, setShowError] = useState(false);

	const {
		value,
		onChange,
		placeholder,
		name,
		error,
		validations = {},
		defaultSelectedOption,
		options: defaultMultiOptions = [],
		label,
		outerLabel = '',
		className = '',
		selectContainerStyle = {},
		controlStyle = () => ({}),
		optionStyle = () => ({}),
		onInputChange = noop,
		onBlur = noop,
		isAsync = false,
		fetchAsyncOptions,
		asyncOptionsCallback,
		getOptionLabel,
		getOptionValue,
		renderOption,
		renderSingleValue,
		Control,
		placeholderStyle,
		readonly = false,
		isMulti = false,
		cacheOptions = true,
	} = props;

	const maxLength = validations?.maxLength as { value: number; message: string };
	const { width } = useViewport();
	const breakpoint = 766;
	const isMobile = width < breakpoint;

	const handleChanges = useCallback((changedValue) => {
		onChange(changedValue);
	}, []);

	const handleClick = useCallback((isDisabled: boolean) => {
		if (isDisabled && validations.maxLength) {
			setShowError(true);
		}
	}, []);

	const Option = (optionProps: OptionProps<any>) => {
		const {
			children,
			data,
			isDisabled
		} = optionProps || {};
		return (
			<div className="makeRelative" onClick={() => handleClick(isDisabled)}>
				<components.Option {...optionProps}>
					{children || data.value}
				</components.Option>
			</div>
		);
	};

	const currentInputValue = value as unknown as { value: string };
	// For multi-select, pass react-select an array of option objects directly;
	// upstream state stores selection as either an array of `{label,value}` or
	// an array of scalar values. Fall back to single-select behavior otherwise.
	const selectedValue = isMulti
		? (Array.isArray(value) ? value : [])
		: (currentInputValue ? { label: currentInputValue?.value || '', ...currentInputValue } : null);

	const selectProps: SelectProps = {
		placeholder: placeholder || label || 'Select an option',
		isMulti,
		theme: (theme) => ({
			...theme,
			colors: {
				...theme.colors,
				primary25: 'transparent',
				primary: 'var(--primary-color)',
			},
		}),
		onBlur,
		onChange: (v) => handleChanges(v),
		value: selectedValue,
		defaultInputValue: defaultSelectedOption?.value || '',
		...(props?.menuPortalTarget ? { menuPortalTarget: props?.menuPortalTarget ?? document.body } : {}),
		styles: {
			placeholder: (defaultStyles) => ({
				...(defaultStyles as React.CSSProperties),
				fontSize: '11px',
				color: 'var(--lightText)',
				fontWeight: '700',
				marginBottom: '3px',
				textTransform: 'uppercase',
				...(placeholderStyle ? placeholderStyle(defaultStyles) : {})
			}),
			container: (base) => ({
				...(base as React.CSSProperties),
				width: isMobile ? '100%' : '363px',
				listStyle: 'none',
				...(error?.message && {
					border: '1px solid #ec2127',
					borderRadius: '4px'
				}),
				...(selectContainerStyle ?? {})
			}),
			option: (base, optionProps) => {
				const { isSelected, isDisabled, isFocused } = optionProps;
				return {
					...(base as React.CSSProperties),
					...((isSelected || isFocused) && {
						background: 'var(--primary-color)',
						color: 'var(--lightPrimaryColor)',
					}),
					...((isFocused && !isSelected) && {
						background: 'var(--lightPrimaryColor)',
						color: 'var(--primary-color)',
					}),
					...(isDisabled && {
						cursor: 'not-allowed'
					}),
					cursor: 'pointer',
					...(optionStyle ? optionStyle(base, optionProps) : {})
				};
			},
			control: (base, optionProps) => ({
				...(base as React.CSSProperties),
				...(controlStyle ? controlStyle(base, optionProps) : {})
			}),
			...props?.styles ?? {}
		},
		name,
		components: {
			Option: renderOption || Option,
			...(renderSingleValue ? { SingleValue: renderSingleValue } : {}),
			...(Control ? { Control } : {})
		},
		options: defaultMultiOptions,
		classNamePrefix: 'react-select',
		onInputChange,
		getOptionLabel,
		getOptionValue,
		isDisabled: readonly,
		isSearchable: isAsync || (defaultMultiOptions?.length ?? 0) > 10
	};

	const loadOptionHandler = useCallback(
		debounce((inputValue: string, callback) => {
			const optionsHandler = (options: Toptions) => {
				if (asyncOptionsCallback) {
					callback(asyncOptionsCallback(options));
				}
			};
			fetchAsyncOptions?.(optionsHandler, defaultMultiOptions, inputValue);
		}, 1000), []
);
	const loadOptions = props.loadOptions || loadOptionHandler; // eslint-disable-line react/destructuring-assignment

	return (
		<div className={`makeRelative appendBottom16 makeFlex column ${isMobile ? 'fullWidth' : ''} ${className}`}>
			{
				outerLabel && label && (
					<div className={`search-select-label ${readonly ? 'search-select-label-readonly' : ''}`}>
						{label ?? ''}
					</div>
				)
			}
			{
				!isAsync ? (
					<Select {...selectProps} />
				) : (
					<AsyncSelect cacheOptions={cacheOptions} defaultOptions loadOptions={loadOptions} {...selectProps} />
				)
			}
			{
				error ? <p className={outerLabel ? 'errorText' : 'errorMsg fieldError'}>{typeof error === 'string' ? error : error?.message}</p> : null
			}
			{
				showError ? <ToastMsg toastFor message={maxLength?.message} onClose={() => setShowError(false)} /> : <></>
			}
		</div>
	);
};

export default React.memo(SearchableSelect);
